export const PROPERTY_FOCUS_MIN_ZOOM = 15;
/** Long enough to read as a zoom, short enough to stay out of the way. */
export const PROPERTY_FOCUS_ANIMATION_MS = 1200;

export type HorizontalSpan = { left: number; right: number };

/** Zoom in to a property, but never zoom out if the user is already closer. */
export function propertyFocusZoom(currentZoom: number): number {
  if (!Number.isFinite(currentZoom)) return PROPERTY_FOCUS_MIN_ZOOM;
  return Math.max(currentZoom, PROPERTY_FOCUS_MIN_ZOOM);
}

/**
 * Pixels to `panBy` on x so a centered pin shifts left, clear of a right-side
 * drawer. Positive x moves the camera east. No shift when the drawer does not
 * overlap the map pane.
 */
export function propertyFocusPanByX(
  mapPane: HorizontalSpan,
  drawer: HorizontalSpan | null,
): number {
  if (!drawer) return 0;
  const overlap =
    Math.min(mapPane.right, drawer.right) - Math.max(mapPane.left, drawer.left);
  if (overlap <= 0) return 0;
  return overlap / 2;
}

export function easeInOut(progress: number): number {
  const t = Math.min(1, Math.max(0, progress));
  return t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
}

/** One frame of a card-click camera move. Zoom is interpolated in level space. */
export function propertyFocusSample(
  progress: number,
  from: { lat: number; lng: number; zoom: number },
  to: { lat: number; lng: number; zoom: number },
) {
  const t = easeInOut(progress);
  return {
    lat: from.lat + (to.lat - from.lat) * t,
    lng: from.lng + (to.lng - from.lng) * t,
    zoom: from.zoom + (to.zoom - from.zoom) * t,
  };
}

/**
 * Shift the final center by `panByX` pixels at `zoom`. World coordinates come
 * from the map projection so the animation can land clear of the drawer in one move.
 */
export function propertyFocusCenter(
  position: { lat: number; lng: number },
  panByX: number,
  zoom: number,
  project: (lat: number, lng: number) => { x: number; y: number } | null,
  unproject: (x: number, y: number) => { lat: number; lng: number } | null,
): { lat: number; lng: number } {
  if (panByX === 0 || !Number.isFinite(zoom)) return position;
  const world = project(position.lat, position.lng);
  if (!world) return position;
  const scale = 2 ** zoom;
  return unproject(world.x + panByX / scale, world.y) ?? position;
}
